// Beds and admissions: how their states move (docs/plans/phase2-inpatient-adt.md §5).
//
// A bed's stored status says what it is when nobody is in it. Whether someone is in it
// comes from the open location row, so "occupied" is derived here and never stored.
//
//   available ──cleaning──▶ cleaning ──available──▶ available
//   available/cleaning ──blocked (reason)──▶ blocked ──available/cleaning──▶ …
//   retired is set and cleared only from Settings (bed.updated), never here.

import { err, ok, type Result } from '../result.ts';

export const BED_STATUSES = ['available', 'cleaning', 'blocked', 'retired'] as const;
export type BedStatus = (typeof BED_STATUSES)[number];

/** What the bed board shows for a bed. */
export const BED_BOARD_STATES = ['occupied', 'free', 'cleaning', 'blocked', 'retired'] as const;
export type BedBoardState = (typeof BED_BOARD_STATES)[number];

export function bedBoardState(status: BedStatus, occupied: boolean): BedBoardState {
  if (occupied) return 'occupied';
  return status === 'available' ? 'free' : status;
}

export type BedStatusChangeErrorCode = 'bed.occupied' | 'bed.reason_required' | 'bed.not_allowed';

/** The statuses a person may set from the bed board. */
export const BOARD_SETTABLE_STATUSES = ['available', 'cleaning', 'blocked'] as const;
export type BoardSettableStatus = (typeof BOARD_SETTABLE_STATUSES)[number];

export function bedStatusChange(input: {
  from: BedStatus;
  to: BoardSettableStatus;
  occupied: boolean;
  reason?: string | null | undefined;
}): Result<BoardSettableStatus, BedStatusChangeErrorCode> {
  if (input.occupied) return err('bed.occupied');
  if (input.from === 'retired' || input.from === input.to) return err('bed.not_allowed');
  if (input.to === 'blocked' && (input.reason ?? '').trim() === '') {
    return err('bed.reason_required');
  }
  return ok(input.to);
}

export const ADMISSION_STATUSES = ['admitted', 'discharged', 'cancelled'] as const;
export type AdmissionStatus = (typeof ADMISSION_STATUSES)[number];

/**
 * TODO(clinical-verify) B9: the provisional disposition list. `died` is recorded here only;
 * the death notification belongs to its own slice.
 */
export const DISCHARGE_DISPOSITIONS = [
  'home',
  'referred',
  'against_medical_advice',
  'absconded',
  'died',
] as const;
export type DischargeDisposition = (typeof DISCHARGE_DISPOSITIONS)[number];

export function admissionTransition(
  from: AdmissionStatus,
  action: 'discharge' | 'cancel',
): Result<AdmissionStatus, 'admission.not_active'> {
  if (from !== 'admitted') return err('admission.not_active');
  return ok(action === 'discharge' ? 'discharged' : 'cancelled');
}
